<script setup lang="ts">
import { ChevronLeft, Search, Sparkle, Sparkles, X } from 'lucide-vue-next'
import { computed } from 'vue'
import { usePejerBase } from '../stores/pejerData'
import { Groups, JSSchema } from '../tables'
import { OperatorObject, SearchQuery } from '../types/types'
import { Badge } from './ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip'

const props = defineProps<{
  filterQuery: SearchQuery<unknown>
  column: string
  value: SearchQuery<unknown>[keyof SearchQuery<unknown>]
  groups?: Groups
  group?: string
  isActive: boolean
  jsSchema: JSSchema
  isImmutable?: boolean
}>()

defineEmits<{
  (e: 'column-select', column: string): void
  (e: 'remove-column', column: string): void
  (e: 'toggle-smart-search', column: string): void
}>()

const store = usePejerBase()
const jsSchemaObject = computed(() => {
  return store.getSchemaReferencedObject(
    store.getSchemaObjectFromFlattenedKeys(props.jsSchema, props.column)
  )
})

function getFormattedValue(column: string, op: '=' | '>' | '<' | '>=' | '<=') {
  const obj = jsSchemaObject.value
  const value = getNormalizedValue(props.filterQuery[column], op)
  if (obj.type == 'number') {
    const formatted =
      obj.metaType == 'money'
        ? parseFloat(value)
            .toLocaleString('nb-NO', {
              style: 'currency',
              currency: 'NOK',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })
            .replace('kr', '')
        : parseFloat(value).toLocaleString('nb-NO')

    return formatted
  }

  if (obj.type == 'date') return new Date(value).toLocaleDateString('nb-NO')

  return value
}

function getNormalizedValue(value: any, operator?: '=' | '>' | '<' | '>=' | '<=') {
  function getNormalizedOperatorValue(value: OperatorObject) {
    if (value === null) return null
    if (typeof value === 'object') return value.value
    return value
  }
  if (operator === '=') if (value === null || typeof value !== 'object') return value

  return getNormalizedOperatorValue(value[operator])
}

const normalizedValue = computed(() => getNormalizedValue(props.value))
</script>
<template>
  <!-- {{ filterQuery }} -->
  <div
    v-if="column === 'OR' || column === 'AND'"
    class="h-full items-center p-0.5 cursor-pointer gap-2 flex text-nowrap bg-white border rounded-sm"
  >
    <template v-for="(searchQueries, index) in (value as SearchQuery<unknown>[])!" :key="index">
      <!-- {{ searchQueries }} ||| -->
      <template v-for="[subColumn, subValue] in Object.entries(searchQueries)" :key="subColumn">
        <!-- (({{ subColumn }}: {{ subValue }})) -->
        <ColumnSearchBadge
          :filter-query="filterQuery[column][index]"
          :value="subValue"
          :column="subColumn"
          :js-schema="jsSchema"
          :groups="groups"
          :group="group"
          :is-active="false"
          isImmutable
        />
      </template>
      <span
        v-if="index < (value as SearchQuery<unknown>[]).length - 1"
        class="font-semibold text-xs"
      >
        {{ column }}</span
      >
    </template>
    <div
      v-if="!isImmutable"
      class="hover:bg-red-200 cursor-pointer rounded-sm flex items-center h-full p-1"
      @click="$emit('remove-column', column)"
    >
      <X :size="12" />
    </div>
  </div>
  <Badge v-else class="p-0.5 w-fit" :variant="isActive ? 'outline' : 'secondary'">
    <div
      class="h-full items-center p-0.5 gap-2 flex text-nowrap"
      :class="{
        'hover:bg-gray-50 cursor-pointer': !isImmutable,
      }"
      @click="$emit('column-select', column)"
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Search
              :size="12"
              :style="{
                color: props.groups?.[group]?.color.accent || 'lightGray',
              }"
            />
          </TooltipTrigger>
          <TooltipContent>
            <p>{{ group }}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <template v-if="getNormalizedValue(value, '=') !== undefined">
        {{ jsSchemaObject.humanReadable }}
        {{ '=' }}
        <span :class="jsSchemaObject.sensitive ? 'sentry-mask' : ''">
          {{
            getNormalizedValue(value, '=') === null ? 'null' : getNormalizedValue(value, '=')
          }}</span
        >
      </template>
      <template v-else>
        <span class="flex gap-2" v-if="getNormalizedValue(value, '>=') !== undefined">
          <span :class="jsSchemaObject.sensitive ? 'sentry-mask' : ''">
            {{ getFormattedValue(column, '>=') }}</span
          ><ChevronLeft :size="12" />
        </span>
        <span class="flex gap-2" v-if="getNormalizedValue(value, '>') !== undefined">
          <span :class="jsSchemaObject.sensitive ? 'sentry-mask' : ''">
            {{ getFormattedValue(column, '>') }}</span
          ><ChevronLeft :size="12" />
        </span>
        {{ jsSchemaObject.humanReadable }}
        <span class="flex gap-2" v-if="getNormalizedValue(value, '<=') !== undefined">
          <ChevronLeft :size="12" />
          <span :class="jsSchemaObject.sensitive ? 'sentry-mask' : ''">
            {{ getFormattedValue(column, '<=') }}
          </span>
        </span>
        <span class="flex gap-2" v-if="getNormalizedValue(value, '<') !== undefined">
          <ChevronLeft :size="12" />
          <span :class="jsSchemaObject.sensitive ? 'sentry-mask' : ''">
            {{ getFormattedValue(column, '<') }}
          </span>
        </span>
      </template>
    </div>
    <template
      v-if="
        getNormalizedValue(value, '=') &&
        value['=']?.smartSearchEnabled !== undefined &&
        jsSchemaObject.type === 'string'
      "
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <div
              v-if="value['=']?.smartSearchEnabled != false"
              class="hover:bg-red-200 rounded-sm flex items-center h-full p-1"
              @click="$emit('toggle-smart-search', column)"
            >
              <Sparkles :size="12" />
            </div>
            <div
              v-else
              class="hover:bg-red-200 rounded-sm flex items-center h-full p-1"
              @click="$emit('toggle-smart-search', column)"
            >
              <Sparkle :size="12" /></div
          ></TooltipTrigger>
          <TooltipContent>
            <p>Smart search ✨</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </template>
    <div
      v-if="!isImmutable"
      class="hover:bg-red-200 cursor-pointer rounded-sm flex items-center h-full p-1"
      @click="$emit('remove-column', column)"
    >
      <X :size="12" />
    </div>
  </Badge>
</template>
